import { noop } from 'angular';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Injector,
	OnDestroy,
	OnInit,
	inject,
	ViewChild
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { VIEWS } from '@go-modules/enums/views';
import { SortOrder, SortOrderTranslations } from '@go-modules/enums/sort-order-main';
import { ScoreFilter, ScoreFilterTranslations } from '@go-modules/enums/score-filter';
import { MinePeerFilter, MinePeerFilterTranslations } from '@go-modules/enums/mine-peer-filter';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	EMPTY,
	filter,
	finalize,
	map,
	Observable,
	Subject,
	switchMap,
	take,
	takeUntil
} from 'rxjs';
import {
	SessionCreationWizardService,
	sessionCreationWizardServiceToken
} from 'go-modules/session-creation-wizard/wizard.service';
import { $qToken } from '@go-modules/upgraded-3rd-party-deps/q.upgrade';
import { ViewStateDataSource } from '@go-modules/services/view-state-datasource/view-state.datasource';
import { Session as SessionModel, sessionToken } from 'go-modules/models/session/session.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import {
	ActivityInstructionsDialogComponent
} from '@go-modules/components/dialogs/activity-Instructions-dialog/activity-instructions-dialog.component';
import { FeedbackMode, MediaDisplayMode } from 'go-modules/feedback-session/feedback-session.provider';
import { FormControl, FormGroup } from '@angular/forms';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { ActivityListDataSource } from '@go-modules/services/activity-list-datasource/activity-list.datasource';
import { FolderListDataSource } from '@go-modules/services/folder-list-datasource/folder-list-datasource';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import { UADetect as UADetectClass, uaDetectToken } from 'go-modules/detect/ua-detect.service';
import {
	ConfirmDialogComponent,
	ConfirmDialogData
} from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { SessionStorageService, sessionStorageToken } from 'go-modules/session-manager/session-storage.service';
import { equipmentCheckModalToken } from 'go-modules/modals/equipment-check/modal.factory';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { MediaSource } from 'go-modules/models/media';
import {
	ActivityInstructionsModal,
	activityInstructionsModalToken
} from 'go-modules/modals/activity-instructions/modal.factory';
import { MODE as INSTRUCTIONS_MODE } from 'go-modules/modals/activity-instructions/modes';
import { ZeroStateChangedEvent } from '../data-source-zero-state/data-source-zero-state.component';
import {
	LicenseExpirationHandler
} from '@go-modules/services/license-expiration-handler/license-expiration-handler.service';
import type { License } from '@go-modules/interfaces/licenses';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { PromptsService } from '@go-modules/services/prompts/prompts.service';
import { StartTestDialogComponent } from '../dialogs/start-test-dialog/start-test-dialog.component';
import { NgxFeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { NgxLicenseUpgradeService } from '../../services/license/license-upgrade/license-upgrade.service';
import * as dayjs from 'dayjs';
import { InviteUsersDialogComponent } from '../dialogs/invite-users-dialog/invite-users-dialog.component';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { BannerType } from '@go-modules/enums/ngx-banner-type';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { PillToggleOption } from '../overview-preview-toggle/pill-toggle.component';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';
import type { Activity } from '@go-modules/interfaces/activity';
import { focusElementWithFallback } from '@go-modules/utilities';
import { ActivityViewRouteA11yService } from './activity-view-route-a11y.service';
import {
	ActivityPreviewEditorDialogService,
	type ActivityPreviewEditorDialogCloseResult
} from '@go-modules/components/dialogs/activity-preview-editor/activity-preview-editor-dialog.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import { ProductTranslationParams } from '@go-modules/interfaces';

export enum TASK_VIEW_OPTIONS {
	OVERVIEW = 'overview',
	PREVIEW = 'preview'
}

@Component({
	selector: 'ngx-activity-view',
	templateUrl: './activity-view.component.html',
	styleUrls: ['./activity-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	host: {
		'[class]': 'responsiveLayout.cssClassString()'
	}
})
export class NgxActivityViewComponent implements OnDestroy, OnInit {
	protected responsiveLayout = inject(ResponsiveLayoutService);

	@ViewChild(MenuComponent) public menuComponent: MenuComponent;
	@ViewChild('activityContainer', {read: ElementRef}) public activityContainer: ElementRef;
	@ViewChild('activityTaskPane') public activityTaskPane: any;
	@ViewChild('leftPane', {read: ElementRef}) public leftPane: ElementRef;
	@ViewChild('rightPane', {read: ElementRef}) public rightPane?: ElementRef<HTMLElement>;

	public views = VIEWS;
	public currentView$: Observable<VIEWS>;
	public sortByOptions: SortOrder[] = Object.values(SortOrder).filter((option) => option !== SortOrder.DEFAULT);
	public sortOrderTranslations = SortOrderTranslations;
	public scoreOptions: ScoreFilter[] = Object.values(ScoreFilter);
	public scoreFilterTranslations = ScoreFilterTranslations;
	public minePeerOptions: MinePeerFilter[] = Object.values(MinePeerFilter);
	public minePeerTranslations = MinePeerFilterTranslations;
	public hasSessions$: Observable<boolean>;
	public shouldShowFooterButtons$: Observable<boolean>;
	public shouldShowTestBailedView$: Observable<boolean>;
	public componentDestroyed$$ = new Subject();
	private onBeforeHookDeregister?: (() => void) | undefined;
	private onSuccessHookDeregister?: (() => void) | undefined;
	public dataSourceZeroState: ZeroStateChangedEvent = {isLoading: true, hasZeroState: false, errorCode: null };
	public form = new FormGroup({
		sortBy: new FormControl(this.sessionListDataSource.getCurrentSort()),
		scoreFilter: new FormControl(this.sessionListDataSource.getCurrentScoreFilter()),
		minePeerFilter: new FormControl(this.sessionListDataSource.getCurrentMinePeerFilter())
	});

	public activityLoaded$ = new BehaviorSubject<boolean>(false);
	public canStartActivity$$ = new BehaviorSubject<boolean>(false);
	public getSessionCreationDisabledMessage$$ = new BehaviorSubject<string>('');
	public environmentVarsService: EnvironmentVarsService;

	public showArrow$ = new BehaviorSubject<boolean>(false);
	public arrowPosition$ = new BehaviorSubject<number>(0);
	public readonly bannerType = BannerType;

	public get isEditMode (): boolean {
		return this.activityEditModeService.isEditMode();
	}
	public pillToggleOptions: PillToggleOption[] = [];
	public selectedOption$ = new BehaviorSubject<string>('');
	public TASK_VIEW_OPTIONS = TASK_VIEW_OPTIONS;

	private activityStarted$$ = new BehaviorSubject<boolean>(false);
	private hasActivatedChildRoute = false;
	private lastRouteAnnouncement: string | null = null;

	public productTranslationParams: ProductTranslationParams;

	constructor (
		public sessionListDataSource: SessionListDataSource,
		public viewStateDataSource: ViewStateDataSource,
		public ngxSessionService: NgxSessionService,
		public ngxTaskService: NgxTaskService,
		private routerService: RouterService,
		private injector: Injector,
		public activityEditModeService: ActivityEditModeService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject($qToken) private $q,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		@Inject(sessionCreationWizardServiceToken) private sessionCreationWizardService: SessionCreationWizardService,
		@Inject(activityToken) private  activityModel,
		@Inject(masqueradeToken) private masquerade: Masquerade,
		private activityListDataSource: ActivityListDataSource,
		private folderListDataSource: FolderListDataSource,
		private modal: GoModalService,
		public ngxActivityService: NgxActivityService,
		private ngxGoToastService: NgxGoToastService,
		@Inject(uaDetectToken) public UADetect: UADetectClass,
		@Inject(sessionStorageToken) private sessionStorage: SessionStorageService,
		@Inject(equipmentCheckModalToken) private equipmentCheckModal,
		@Inject(mediaToken) private MediaModel,
		@Inject(activityInstructionsModalToken) private activityInstructionsModal: ActivityInstructionsModal,
		private licenseExpirationHandler: LicenseExpirationHandler,
		private dialog: MatDialog,
		private titleService: Title,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
		private promptsService: PromptsService,
		private featureFlag: NgxFeatureFlagService,
		private ngxLicenseUpgradeService: NgxLicenseUpgradeService,
		private route: ActivatedRoute,
		private router: Router,
		private liveAnnouncer: LiveAnnouncer,
		private activityViewRouteA11yService: ActivityViewRouteA11yService,
		private activityPreviewEditorDialog: ActivityPreviewEditorDialogService,
		public activeBrandService: ActiveBrandService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public isLti (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public ngOnInit (): void {
		this.productTranslationParams = this.activeBrandService.getTranslationParams();
		this.updatePageTitle(this.selectedService.getActivity()?.name);
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				map((selected: any) => selected.activity?.name ?? null),
				distinctUntilChanged(),
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe((activityName) => this.updatePageTitle(activityName));

		// Load activity if not already loaded
		if (this.selectedService.getActivity() === null) {
			this.activityListDataSource.connect().subscribe((activities) => {
				const activity = activities.find((act) => {
					return Number(act.activity_id) === Number(this.routerService.getCurrentParams().activity_id);
				});

				if (activity) {
					this.selectedService.setActivity(activity);
					this.setUp();
					this.activityLoaded$.next(true);
				// fallback to folder view in case we don't have the activity
				} else {
					this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, {
						folder_id: this.selectedService.getGroup().group_id
					});
				}
			});
		} else {
			this.activityLoaded$.next(true);
			this.setUp();
		}

		// Initialize pill toggle options
		this.pillToggleOptions = [
			{
				value: TASK_VIEW_OPTIONS.OVERVIEW,
				label: this.translate.instant('activity-view_pill-overview'),
				class: 'instructor-overview-pill'
			},
			{
				value: TASK_VIEW_OPTIONS.PREVIEW,
				label: this.translate.instant('activity-view_pill-preview'),
				class: 'activity-preview-pill'
			}
		];

		// Initialize route handlers and set initial state
		this.initRouteHandlers();
	}

	private updatePageTitle (activityName: string | null | undefined): void {
		if (activityName == null) {
			return;
		}
		this.titleService.setTitle(
			this.translate.instant('document-title_dashboard', { name: activityName, product: this.productTranslationParams.product })
		);
	}

	private initRouteHandlers (): void {
		const currentRoute = this.routerService.getCurrentRouteName();
		const currentParams = this.routerService.getCurrentParams();

		// Watch for edit mode changes via query params
		this.route.queryParams
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe((params) => {
				const isEditMode = params.mode === 'edit';

				if (this.isActivityPreviewEditorDialogEnabled()) {
					if (isEditMode) {
						this.activityEditModeService.exitEditMode();
						this.removeEditModeQueryParam();
					}
					return;
				}

				if (isEditMode && !this.activityEditModeService.isEditMode()) {
					const activity = this.selectedService.getActivity();
					if (activity) {
						this.ngxTaskService.getTasks(activity.activity_id)
							.pipe(take(1))
							.subscribe((tasks: Task[]) => {
								const formattedTasks = tasks.map((task) => {
									return {
										...task,
										name: this.getTaskName(activity, task)
									};
								});

								this.activityEditModeService.enterEditMode(formattedTasks);
							});
					}
				} else if (!isEditMode && this.activityEditModeService.isEditMode()) {
					this.activityEditModeService.exitEditMode();
				}
			});

		// Watch for route changes to update pill toggle
		const router = this.injector.get(Router);
		router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe(() => {
				const routeName = this.routerService.getCurrentRouteName();
				const routeParams = this.routerService.getCurrentParams();
				this.updatePillToggleForRoute(routeName, routeParams);
				if (this.shouldHandleRouteAnnouncementOnActivate(routeName)) {
					return;
				}

				this.announceCurrentRoute(routeName, routeParams);
			});

		// Set initial pill toggle state
		this.updatePillToggleForRoute(currentRoute, currentParams);
	}

	private updatePillToggleForRoute (routeName: string, routeParams: any): void {
		// Store the current route in sessionStorage so we can return to it after exiting feedback session
		if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK || routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW) {
			sessionStorage.setItem('previousActivityRoute', JSON.stringify({
				routeName,
				params: routeParams
			}));
		}

		if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK && routeParams.type === 'evaluate') {
			// /tasks/evaluate is the Submissions route (Instructor Overview)
			this.selectedOption$.next(TASK_VIEW_OPTIONS.OVERVIEW);
		} else if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK && routeParams.type) {
			// Other tasks are Activity Preview
			this.selectedOption$.next(TASK_VIEW_OPTIONS.PREVIEW);
		} else if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW ||
		           routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT ||
		           routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT) {
			// Overview routes
			this.selectedOption$.next(this.hasInstructorRole() ?
				TASK_VIEW_OPTIONS.OVERVIEW : TASK_VIEW_OPTIONS.PREVIEW);
		}
	}

	public setUp () {
		this.currentView$ = this.viewStateDataSource.viewState$;
		this.hasSessions$ = this.sessionListDataSource.connect()
			.pipe(map((session) => session.length > 0));
		this.shouldShowTestBailedView$ = combineLatest([
			this.sessionListDataSource.connect(),
			this.activityStarted$$
		]).pipe(
			map(([sessions, activityStarted]) => {
				const baseCondition = this.selectedService.getActivity().has_single_recording_attempt &&
					this.selectedService.getGroup().hasPresenterRole() &&
					sessions.some(
						(session: ReturnType<typeof SessionModel>) => (
							!session.archived_at &&
							!session.started_at &&
							session.isOwner(this.userService.currentUser) &&
							session.hasViewedRecordingInstructions()
						)
					);

				// Do not show view if activity started but navigation not complete
				// Otherwise, show immediately if base conditions are met
				return baseCondition && (!activityStarted);
			})
		);
		this.shouldShowFooterButtons$ = combineLatest({
			hasSessions: this.hasSessions$,
			isFiltered: this.sessionListDataSource.isFiltered$,
			shouldShowTestBailedView: this.shouldShowTestBailedView$
		}).pipe(
			map(({hasSessions, isFiltered, shouldShowTestBailedView}) =>
				(hasSessions || isFiltered) && !shouldShowTestBailedView
			)
		);

		this.form.get('sortBy').valueChanges
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe((selected) => this.sessionListDataSource.setSortBy(selected));
		this.form.get('scoreFilter').valueChanges
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe((selected) => this.sessionListDataSource.setScoreFilter(selected));
		this.form.get('minePeerFilter').valueChanges
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe((selected) => this.sessionListDataSource.setMinePeerFilter(selected));

		if (!this.shouldShowMinePeerFilter()) {
			this.form.get('minePeerFilter').setValue(MinePeerFilter.ALL);
		}

		// TODO DEV-15860
		if (!this.selectedService.getActivity().viewed_by_me &&
			!this.masquerade.isMasked() &&
			this.hasPresenterRole()) {
			this.activityModel
				.markAsViewed({activity_id: this.selectedService.getActivity().activity_id})
				.$promise.then(() => {
					this.activityListDataSource.reloadStats();
					this.folderListDataSource.reloadStats();
				});
		}

		this.getLicenseAndValidate();
		this.promptsService.showTermsOrTourWhenRequired();
	}

	public dismissBanner () {
		localStorage.setItem('activityTaskPreviewBanner', 'dismissed');
	}

	public onOptionSelect (option: string) {
		const activity = this.selectedService.getActivity();
		if (!activity) {
			return;
		}

		// Save previous value in case we need to revert (if guard blocks navigation)
		const previousValue = this.selectedOption$.value;

		// Update selectedOption$ eagerly to keep Material button-toggle in sync
		// This prevents out-of-sync issues when guards block navigation
		this.selectedOption$.next(option);

		if (option === TASK_VIEW_OPTIONS.PREVIEW) {
			// For preview mode, navigate to the first incomplete default task
			this.ngxTaskService.getTasks(activity.activity_id)
				.pipe(take(1))
				.subscribe((tasks) => {
					if (!tasks || tasks.length === 0) {
						return;
					}
					const defaultTasks = this.ngxTaskService.getDefaultTasks(activity);
					const defaultTaskTypes = defaultTasks.map((task) => task.type);
					const filteredTasks = tasks
						.filter((task) => {
							return defaultTaskTypes.includes(task.type) && !task.deleted_at;
						})
						.sort((a, b) => a.sort_order - b.sort_order);
					const targetTask = filteredTasks.find((task) => !task.completed) || filteredTasks[0] || tasks[0];

					if (targetTask) {
						const navigationPromise = this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
							activity_id: activity.activity_id,
							type: targetTask.type
						});
						// navigation failure (guard blocked it)
						this.handleNavigationResult(navigationPromise, previousValue);
					}
				});
		} else {
			// For overview mode, navigate to Submissions (/tasks/evaluate)
			const navigationPromise = this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
				activity_id: activity.activity_id,
				type: 'evaluate',
				mode: null
			});

			// Handle navigation failure (guard blocked it)
			this.handleNavigationResult(navigationPromise, previousValue);
		}
	}

	private handleNavigationResult (navigationPromise: any, previousValue: string) {
		// Angular Router: router.navigate() resolves to false on guard block
		navigationPromise.then(
			(result) => {
				// Angular Router returns boolean, UI Router returns transition
				if (result === false) {
					this.selectedOption$.next(previousValue);
				}
			},
			() => {
				// Promise rejected (UI Router guard block)
				this.selectedOption$.next(previousValue);
			}
		);
	}

	public showPreviewBanner (): boolean {
		return this.ngxTaskService.shouldShowTasks() &&
			this.hasInstructorRole() &&
			this.selectedOption$.value === TASK_VIEW_OPTIONS.PREVIEW &&
			localStorage.getItem('activityTaskPreviewBanner') == null;
	}

	public ngOnDestroy () {
		if (this.onBeforeHookDeregister) {
			this.onBeforeHookDeregister();
		}
		if (this.onSuccessHookDeregister) {
			this.onSuccessHookDeregister();
		}
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
		this.canStartActivity$$.complete();
		this.getSessionCreationDisabledMessage$$.complete();
	}

	public hasReviewerRoleOrHigher (): boolean  {
		return this.selectedService.getGroup().hasReviewerRole(true);
	}

	public hasPresenterRole (): boolean  {
		return this.selectedService.getGroup().hasPresenterRole();
	}

	public hasInstructorRole (): boolean {
		return this.selectedService.getGroup().hasInstructorRole(true);
	}

	public activityNotAvailable (): boolean {
		const availableAt = dayjs.utc(this.selectedService.getActivity().available_at);
		const nowUtc = dayjs.utc();

		return this.hasPresenterRole() && availableAt.isAfter(nowUtc);
	}

	public getActivityAvailableAt (): Date {
		return this.selectedService.getActivity().available_at;
	}

	public canInvite (): boolean {
		return !this.isLti() && (this.hasInstructorRole() || this.userService.currentUser.is_root_user);
	}

	public goToSession (session) {
		this.selectedService.setSession(this.Session.model(session));
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, { session_id: session.session_id });
	}

	public openInviteModal () {
		InviteUsersDialogComponent.open(this.dialog, {
			data: {
				group: this.selectedService.getGroup()
			}
		});
	}

	public preStartActivity () {
		if (this.canStartActivity$$.value === false) {
			return;
		}

		const activity = this.selectedService.getActivity();

		if (activity.isCommentOnlySingleAttempt()) {
			this.modal.open(StartTestDialogComponent, false, {
				data: {
					currentActivity: activity
				}
			}).afterClosed().subscribe((confirm) => {
				if (confirm) {
					this.startActivity();
				}
			});
		} else {
			this.startActivity();
		}
	}

	public startActivity () {
		// clear out any previously selected session to prevent issues with recording
		// sessionCreationWizardService promise doesnt resolve until after recording instructions
		// are viewed, which happens after we go to the record screen
		// and we don't set the new session until after that
		this.selectedService.setSession(null);
		const activity = this.selectedService.getActivity();
		const group = this.selectedService.getGroup();

		return this.sessionCreationWizardService.start({
			activity,
			group,
			user: this.userService.currentUser,
			openRecordView: (session) => {
				this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
					session_id: session.session_id
				}, {
					queryParams: {
						feedbackMode: FeedbackMode.VIEW,
						viewMode: MediaDisplayMode.RECORD
					}
				});
				return this.$q.resolve();
			}
		}).then((result) => {
			this.activityStarted$$.next(true);
			result.viewed = true;
			const session = this.Session.model(result);
			this.selectedService.setSession(session);
			this.sessionListDataSource.addSession(session);

			if (this.hasPresenterRole()) {
				this.activityListDataSource.reloadStats();
				this.folderListDataSource.reloadStats();
				// dont allow second submission for single attempt
				if (this.selectedService.getActivity().has_single_recording_attempt) {
					this.getLicenseAndValidate();
				}
			}

			if (activity.isCommentOnly() || activity.isConversationBoard()) {
				this.goToSession(session);
			}
			return result;
		}).catch(noop);
	}

	public viewInstructions () {
		this.modal.open(ActivityInstructionsDialogComponent);
	}

	public shouldShowScoreFilter (): boolean {
		return this.hasReviewerRoleOrHigher() &&
			this.selectedService.getActivity().total_score > 0;
	}

	/**
	 * We show the mine peer filter for peer_review, but not for public_feedback. Why?
	 * These two settings control the permissioning on what peers see
	 * peer_review === false && public_feedback === false: Private
	 * peer_review === true && public_feeback === false: Closed Peer Review
	 * peer_review === true && public_feedback === true: Open Peer Review for Standard/Stimulus (has_response_media)
	 * peer_review === false && public_feedback === true: Open Peer Review for Comment Only (!has_response_media)
	 * In comment-only peer-review, other sessions are filtered out and the feedback-viewer is responsible for combining
	 * every's comments in the activity together.
	 *
	 * Additionally we show it on peer_rubric_critique_enabled when a peer rubic is added, and
	 * require has_response_media to prevent conversation board from seeing the filter.
	 */
	public shouldShowMinePeerFilter (): boolean {
		const activity = this.selectedService.getActivity();

		return this.hasPresenterRole() &&
			(activity.peer_review || activity.peer_rubric_critique_enabled) &&
			activity.has_response_media;
	}

	public shouldShowFeedbackHold () {
		const activity = this.selectedService.getActivity();
		return this.hasInstructorRole() && activity && activity.isViewable('is_feedback_published');
	}

	public openSession (session) {
		const activity = this.selectedService.getActivity();
		if (!session.isRecorded() && activity.isConversation() &&
			this.ngxSessionService.doesGroupSessionHaveMaxPresenterCount(session)) {
			return this.showSessionFullModal();
		} else if (activity.isSingleAttempt() && !activity.isCommentOnly() && !session.media) {
			return this.showFailedSingleAttemptModal();
		}

		this.enterSession(session);
	}

	private showFailedSingleAttemptModal () {
		MessageDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('single-attempt-activity_no-recording'),
				message: this.translate.instant('feedback-session-failed-single-attempt')
			}
		});
	}

	public enterSession (session) {
		const activity = this.selectedService.getActivity();

		// live multi cam (group recording && live review)
		// prompt the user if they would like to be a presenter or observer
		if (activity.isMulticamLiveReview() && !session.isPosted()) {
			// If the group is already full, skip asking the user what they want and navigate to session
			if (!session.hasMaxPresenterCount(activity)) {
				this.confirmParticipation(session);
			}
			else {
				this.goToSession(session);
			}
		}
		// live review
		else if (activity.isLiveSessionEnabled() &&
			!activity.isConversation() &&
			session.isAwaitingStart() &&
			this.mayStart(session)
		) {
			this.confirmParticipation(session, true);
		}
		// group recording
		else if (activity.isConversation() &&
			!this.ngxSessionService.doesGroupSessionHaveMaxPresenterCount(session) &&
			!session.isRecorded()
		) {
			this.checkEquipment(session);
		}
		else {
			this.goToSession(session);
		}
	}

	public zeroStateChanged (event: ZeroStateChangedEvent) {
		this.dataSourceZeroState = event;
		this.cdr.detectChanges();
	}

	public openMenu (event: MouseEvent) {
		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}

		if (this.canStartActivity$$ && !this.selectedService.getActivity().isConversationBoard()) {
			this.menuComponent.openMenu(event, this.activityContainer.nativeElement);
		}
	}

	public onTaskSelected (): void {
		this.showArrow$.next(true);
		setTimeout(() => {
			this.updateArrowPosition();
		}, 0);
	}

	public onNavigationSelected (): void {
		this.showArrow$.next(true);
		setTimeout(() => {
			this.updateArrowPosition();
		}, 0);
	}

	public onLeftPaneScroll (): void {
		if (this.showArrow$.value) {
			this.updateArrowPosition();
		}
	}

	public setNextRouteAnnouncementPoliteness (politeness: 'polite' | 'assertive'): void {
		this.activityViewRouteA11yService.setNextRouteAnnouncementPoliteness(politeness);
	}

	public setNextRouteFocusSelector (selector: string): void {
		this.activityViewRouteA11yService.setNextRouteFocusSelector(selector);
	}

	public onRouteActivate (): void {
		const routeName = this.routerService.getCurrentRouteName();
		const shouldHandleRouteAnnouncement = this.shouldHandleRouteAnnouncementOnActivate(routeName);
		this.hasActivatedChildRoute = true;
		if (!shouldHandleRouteAnnouncement) {
			return;
		}

		const routeParams = this.routerService.getCurrentParams();
		void this.restorePendingRouteFocus().then(() => {
			setTimeout(() => {
				this.announceCurrentRoute(routeName, routeParams);
			}, 0);
		});
	}

	private getTaskName (activity, task: Task): string {
		return this.ngxActivityService.getTaskTitleAndDescription(activity, task).title;
	}

	private announceCurrentRoute (routeName: ROUTE_NAMES | null, routeParams: Record<string, any>): void {
		const politeness = this.activityViewRouteA11yService.consumeNextRouteAnnouncementPoliteness() ?? 'polite';
		const announcement = this.getRouteAnnouncement(routeName, routeParams);
		if (announcement == null) {
			this.lastRouteAnnouncement = null;
			return;
		}

		if (announcement === this.lastRouteAnnouncement) {
			return;
		}

		this.lastRouteAnnouncement = announcement;
		void this.liveAnnouncer.announce(announcement, politeness);
	}

	private restorePendingRouteFocus (): Promise<void> {
		const selector = this.activityViewRouteA11yService.consumeNextRouteFocusSelector();
		if (selector == null) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			let attemptsRemaining = 20;
			const focusTarget = (): void => {
				const root = this.rightPane?.nativeElement;
				const target = root?.querySelector(selector);
				if (!(target instanceof HTMLElement)) {
					attemptsRemaining -= 1;
					if (attemptsRemaining <= 0) {
						resolve();
						return;
					}

					setTimeout(() => {
						focusTarget();
					}, 25);
					return;
				}

				focusElementWithFallback(target);

				setTimeout(() => {
					const refreshedTarget = this.rightPane?.nativeElement.querySelector(selector);
					if (refreshedTarget instanceof HTMLElement &&
						refreshedTarget.ownerDocument?.activeElement === refreshedTarget) {
						resolve();
						return;
					}

					attemptsRemaining -= 1;
					if (attemptsRemaining <= 0) {
						resolve();
						return;
					}

					focusTarget();
				}, 25);
			};

			focusTarget();
		});
	}

	private shouldHandleRouteAnnouncementOnActivate (routeName: ROUTE_NAMES | null): boolean {
		if (!this.hasActivatedChildRoute) {
			return true;
		}

		if (this.activityViewRouteA11yService.peekNextRouteFocusSelector() != null) {
			return true;
		}

		return routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT ||
			routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT;
	}

	private getRouteAnnouncement (routeName: ROUTE_NAMES | null, routeParams: Record<string, any>): string | null {
		const title = this.getRouteAnnouncementTitle(routeName, routeParams);
		return title == null
			? null
			: this.translate.instant('activity-view_route-announcement-page', { title });
	}

	private getFallbackTaskTitle (taskType: TaskType): string | null {
		switch (taskType) {
			case TaskType.Present:
				return this.translate.instant('task-title_present-standard');
			case TaskType.Reflect:
				return this.translate.instant('task-title_reflect');
			case TaskType.Submit:
				return this.translate.instant('task-title_submit');
			case TaskType.Review:
				return this.translate.instant('task-title_review');
			case TaskType.React:
				return this.translate.instant('task-title_react');
			default:
				return null;
		}
	}

	private getRouteAnnouncementTitle (routeName: ROUTE_NAMES | null, routeParams: Record<string, any>): string | null {
		if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT) {
			return this.translate.instant('task-report_header-title');
		}

		if (routeName === ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT) {
			return this.translate.instant('activity-report_header-title');
		}

		if (routeName !== ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK) {
			return null;
		}

		if (routeParams.type === TaskType.Evaluate) {
			return this.translate.instant('common_submissions');
		}

		const activity = this.selectedService.getActivity();
		if (activity == null || routeParams.type == null) {
			return null;
		}

		const task = this.activityEditModeService.currentTasks().find(
			(currentTask) => currentTask.type === routeParams.type
		) ?? { type: routeParams.type, name: null } as Task;

		try {
			return this.ngxActivityService.getTaskTitleAndDescription(activity, task).title;
		} catch {
			return this.getFallbackTaskTitle(routeParams.type as TaskType);
		}
	}

	private updateArrowPosition (): void {
		const activeTaskElement = document.querySelector('.task-item.active') as HTMLElement;
		const containerElement = document.querySelector('.task-view-container');

		if (activeTaskElement && containerElement) {
			const taskRect = activeTaskElement.getBoundingClientRect();
			const containerRect = containerElement.getBoundingClientRect();

			const relativeTop = taskRect.top - containerRect.top;
			const taskHeight = taskRect.height;
			const newPosition = relativeTop + (taskHeight / 2) - 15;

			this.arrowPosition$.next(newPosition);
		} else {
			this.arrowPosition$.next(0);
			this.showArrow$.next(false);
		}
	}

	private confirmParticipation (session, isLiveReview = false) {
		const user = this.userService.currentUser;
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('confirm-join-title'),
				message: this.translate.instant('confirm-join-message'),
				confirmText: this.translate.instant('confirm-join-button-as-presenter'),
				cancelText: this.translate.instant('confirm-join-button-as-observer')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				if (isLiveReview) {
					this.startLiveReview(session);
				} else {
					this.checkEquipment(session);
				}
			}
			// confirm dialog can return null on close
			// explicitly check if false is returned
			else if (confirm === false){
				session.removePresenter(user);
				this.sessionListDataSource.editSession(session);
				this.goToSession(session);
			}
		});
	}

	private startLiveReview (session) {
		let startPromise = Promise.resolve(null);

		// If media has not been created yet, pre-create the media and assign it to the session.
		// This should only get run when the session was created as a part of a batch creation process.
		// When the Live Review setting is enabled on an assignment,
		// the Instructor can click a button that creates a batch of sessions
		if (!session.media) {
			startPromise = new Promise(async (resolve, _reject) => {
				// MediaModel.createFor() contains async code
				const media = await this.MediaModel.createFor(MediaSource.OPENTOK, session.group_id);
				session.setMedia(media);
				session.save();
				resolve(null);
			});
		}

		startPromise.then(() => {
			this.showInstructionsThenGoToSession(session);
		});
	}

	private showInstructionsThenGoToSession (session) {
		const activity = this.selectedService.getActivity();
		const user = this.userService.currentUser;

		if (activity.hasRecordingInstructions() || this.isHeadPhonesInstructionsRequired(session)) {
			const group = this.selectedService.getGroup();
			const embiggen = activity.hasRecordingInstructions() &&
				!!activity.recording_instructions.media &&
				!group.license_trial_exists;
			const modalInstance = this.activityInstructionsModal.open({
				focusReturnSelector: '[data-cy="comment-tab-button"]',
				modalData: {
					activity,
					mode: INSTRUCTIONS_MODE.RECORDING,
					showHeadPhones: this.isHeadPhonesInstructionsRequired(session),
					embiggen
				}
			});
			modalInstance.opened
				.then(() => {
					if (!session.hasViewedRecordingInstructions()) {
						session.setViewedRecordingInstructions();
					}
				});

			modalInstance.result.then(() => {
				this.addPresenterThenGoToRecordSession(session, user);
			});
		} else {
			this.addPresenterThenGoToRecordSession(session, user);
		}
	}

	private addPresenterThenGoToRecordSession (session, user) {
		return Promise.resolve(session.addPresenter(user)).then(() => {
			this.sessionListDataSource.editSession(session);
			this.selectedService.setSession(session);
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_SESSION_VIEW, {
				session_id: session.session_id
			}, {
				queryParams: {
					feedbackMode: FeedbackMode.VIEW,
					viewMode: MediaDisplayMode.RECORD
				}
			});
		});
	}

	private isHeadPhonesInstructionsRequired (session) {
		const media = session.source_media || this.selectedService.getActivity().source_media?.media;
		return !!media && media.hasAudio();
	}

	private mayStart (session) {
		const activity = this.selectedService.getActivity();
		const group = this.selectedService.getGroup();
		const user = this.userService.currentUser;
		let hasPermission = session.mayPublish(user, group, activity);

		if (session.source_media?.isPending()) {
			hasPermission = false;
		}

		return hasPermission;
	}

	private checkEquipment (session) {
		const activity = this.selectedService.getActivity();
		const isEquipmentCheckRequired = activity.isEquipmentCheckRequired(this.sessionStorage);

		if (isEquipmentCheckRequired) {
			try {
				const equipmentCheck: any = this.equipmentCheckModal.open({
					activity,
					session,
					focusReturnSelector: '[data-cy="add-video"]'
				});
				equipmentCheck.result
					.then(() => {
						this.showInstructionsThenGoToSession(session);
					});
			} catch (e) {
				MessageDialogComponent.open(this.dialog, {
					data: {
						title: this.translate.instant('unknown-error-title'),
						message: this.translate.instant('group-view_zero-state-unexpected-error')
					}
				});
			}
		} else {
			this.showInstructionsThenGoToSession(session);
		}
	}

	private showSessionFullModal (): void {
		MessageDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('session-list-item_controller-max-presenter-title'),
				message: this.translate.instant('session-list-item_controller-max-presenter-message')
			}
		});
	}

	private validateCanStartActivity () {
		const canStartActivity = this.ngxActivityService.canStartActivity();
		this.canStartActivity$$.next(canStartActivity);
		this.getSessionCreationDisabledMessage$$.next(this.ngxActivityService.getSessionCreationDisabledMessage());

		const license = this.selectedService.getLicense();
		if (license) {
			// TODO STAB-489 Consolidate ngx/ng1 License interfaces since we're already using them interchangeably
			this.licenseExpirationHandler.displayLicenseExpirationWarningToastIfNeeded(license as License);
		}
	}

	private getLicenseAndValidate () {
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.componentDestroyed$$),
				filter((selected: any) => selected.group)
			)
			.subscribe((selected) => {
				if (this.isLti() &&
					this.hasInstructorRole() &&
					this.featureFlag.isAvailable('AI_TRIAL') &&
					selected.group.license_trial_exists &&
					selected.license?.salesforce_license.is_free_trial &&
					(!selected.license.is_active || dayjs().isAfter(dayjs.utc(selected.license.ends_at)))) {
					this.handleExpiredLicenseTrial(selected.license).subscribe();
				}
				this.validateCanStartActivity();
			});
	}

	private handleExpiredLicenseTrial (license: License) {
		return this.ngxLicenseUpgradeService.upgradeOrPurchase(license.trial.previous_license, true, true, true)
			.pipe(
				switchMap(({requestUpgrade, shouldRestart}) => {
					if (shouldRestart) {
						return this.handleExpiredLicenseTrial(license);
					} else if (requestUpgrade) {
						// Non license-admin requested an upgrade
						return this.ngxLicenseUpgradeService
							.handleLicenseUpgradeRequest(license.trial.previous_license.id);
					} else {
						// License admin successfully upgraded
						return EMPTY;
					}
				}),
				takeUntil(this.componentDestroyed$$)
			);
	}

	public toggleEditMode (): void {
		const currentParams = this.routerService.getCurrentParams();
		const currentQueryParams = this.routerService.getCurrentQueryParams();
		const currentRoute = this.routerService.getCurrentRouteName();

		if (!this.isEditMode && this.isActivityPreviewEditorDialogEnabled()) {
			this.openActivityPreviewEditorDialog();
			return;
		}

		if (this.isEditMode) {
			// Exiting edit mode - check for unsaved changes and save
			if (this.activityEditModeService.hasUnsavedChanges()) {
				const activity = this.selectedService.getActivity();

				// Check if there are existing sessions and show warning modal
				if (activity.usage && activity.usage > 0) {
					ConfirmDialogComponent.open(this.dialog, {
						data: {
							title: this.translate.instant('modal-activity-edit-warning_edit-activity'),
							message: this.translate.instant('modal-activity-edit-warning_warning-text', {
								total: activity.usage
							}),
							confirmText: this.translate.instant('common_continue')
						}
					}).afterClosed().subscribe((confirmed: boolean) => {
						if (confirmed) {
							this.performActualSave();
						}
					});
				} else {
					// No sessions, proceed with save
					this.performActualSave();
				}
			} else {
				// Dashboard: Remove mode from query params
				const remainingParams = this.getQueryParamsWithoutEditMode(currentQueryParams);
				this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
					queryParams: remainingParams
				});
			}
		} else {
			// Dashboard: Add mode to query params
			this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
				queryParams: {...currentQueryParams, mode: 'edit'}
			});
		}
	}

	private performActualSave (): void {
		const currentParams = this.routerService.getCurrentParams();
		const currentQueryParams = this.routerService.getCurrentQueryParams();
		const currentRoute = this.routerService.getCurrentRouteName();

		// Check if additional settings form is valid before saving
		if (!this.activityEditModeService.validateAdditionalSettingsForm()) {
			// If not on Present task route, navigate there and revalidate form to show validation errors
			if (currentParams.type !== TaskType.Present) {
				this.router.events
					.pipe(
						filter((event) => event instanceof NavigationEnd),
						take(1),
						takeUntil(this.componentDestroyed$$)
					)
					.subscribe(() => {
						setTimeout(() => {
							this.activityEditModeService.validateAdditionalSettingsForm();
						});
					});
				this.navigateToPresentTaskRoute(currentParams, currentQueryParams, currentRoute);
			}
			return;
		}

		this.activityEditModeService.setIsSaving(true);

		const editedActivity = this.activityEditModeService.currentActivity();
		const editedTasks = this.activityEditModeService.currentTasks();

		if (!editedActivity) {
			this.activityEditModeService.setIsSaving(false);
			return;
		}

		const activityWithTasks = {
			...editedActivity,
			tasks: editedTasks
				.filter((task) => !task.deleted_at)
				.map((task) => this.removeModifier(editedActivity, task))
		};

		this.ngxActivityService.update(activityWithTasks).pipe(
			finalize(() => {
				this.activityEditModeService.setIsSaving(false);
			})
		).subscribe({
			next: (updatedActivity) => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'folder-view_activity_updated'
				});

				this.activityEditModeService.commitChanges(updatedActivity);
				this.ngxTaskService.updateTasks(editedActivity.activity_id, editedTasks);

				// Remove edit mode param after save
				const remainingParams = this.getQueryParamsWithoutEditMode(currentQueryParams);
				this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
					queryParams: remainingParams
				});
			},
			error: () => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common_unknown-error_message'
				});
			}
		});
	}

	private isActivityPreviewEditorDialogEnabled (): boolean {
		return this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	private openActivityPreviewEditorDialog (): void {
		const activity = this.selectedService.getActivity();
		if (activity == null) {
			return;
		}

		const currentParams = this.routerService.getCurrentParams();
		const currentActivityId = Number(currentParams.activity_id);
		if (currentActivityId && currentActivityId !== Number(activity.activity_id)) {
			return;
		}

		this.activityPreviewEditorDialog.open({
			mode: 'edit',
			activityId: activity.activity_id,
			activityDraft: activity,
			initialTaskType: currentParams.type as TaskType | undefined,
			group: this.selectedService.getGroup(),
			account: this.selectedService.getAccount(),
			org: this.selectedService.getOrg(),
			license: this.selectedService.getLicense(),
			environment: this.environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT),
			syncSelectedActivity: {
				activityId: activity.activity_id,
				sync: (savedActivity: Activity) => {
					if (this.selectedService.getActivity()?.activity_id !== savedActivity.activity_id) {
						return;
					}

					this.selectedService.setActivity(this.activityModel.model(savedActivity));
				}
			}
		}).then((dialogRef) => {
			dialogRef.afterClosed()
				.pipe(take(1))
				.subscribe((result: ActivityPreviewEditorDialogCloseResult) => {
					if (result == null || 'dismissed' in result) {
						return;
					}

					const updatedActivity = this.activityModel.model(result.activity);
					this.activityListDataSource.editActivity({ ...updatedActivity });
					this.cdr.markForCheck();
				});
		})
			.catch(noop);
	}

	private removeEditModeQueryParam (): void {
		const currentParams = this.routerService.getCurrentParams();
		const currentQueryParams = this.routerService.getCurrentQueryParams();
		const currentRoute = this.routerService.getCurrentRouteName();
		const remainingParams = this.getQueryParamsWithoutEditMode(currentQueryParams);

		this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
			queryParams: remainingParams,
			replaceUrl: true
		});
	}

	private getQueryParamsWithoutEditMode (queryParams: Params): Params {
		const remainingParams = { ...queryParams };
		delete remainingParams.mode;

		return remainingParams;
	}

	private navigateToPresentTaskRoute (currentParams: any, currentQueryParams: any, currentRoute: string): void {
		const params = {
			...currentParams,
			type: TaskType.Present
		};

		this.routerService.navigateTo(currentRoute as ROUTE_NAMES, params, {
			queryParams: currentQueryParams
		});
	}

	private removeModifier (activity: Activity, task: Task) {
		if (task.name) {
			const defaultTitleAndDescription = this.ngxActivityService.getTaskTitleAndDescription(
				activity,
				{ ...task, name: null } as Task
			);

			if (task.name === defaultTitleAndDescription.title) {
				return { ...task, name: null };
			}
		}

		return task;
	}
}
